<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<!-- eslint-disable vue/no-root-v-if -->
<template>
  <div v-if="path" class="p-breadcrumb p-component">
    <div class="p-breadcrumb-list">
      <!-- Home Icon -->
      <div class="p-menuitem">
        <router-link :to="path[0].to" :aria-label="$t('home')">
          <span
            class="ri-home-4-line breadcrumbIcon primary-color"
            aria-hidden="true"
          />
        </router-link>
      </div>
      <i
        class="ri-arrow-right-s-line breadcrumbSeparator text-2xl mb-2 mx-1"
      ></i>
      <!-- Intermediate Path (only for large screen) -->
      <div v-if="!isVerySmallScreen">
        <div class="p-menuitem">
          <router-link :to="path[1].to" class="flex align-items-center">
            <span
              class="ri-building-line breadcrumbIcon primary-color"
              aria-hidden="true"
            >
            </span>
            <span
              v-tooltip="path[1].name"
              class="commonElementName breadcrumbElementName"
            >
              {{ path[1].name }}
            </span>
          </router-link>
        </div>
      </div>

      <i
        v-if="!isVerySmallScreen"
        class="ri-arrow-right-s-line breadcrumbSeparator text-2xl mb-2 mx-1"
      ></i>

      <!-- Breadcrumb Dropdown -->
      <PrimeButton
        type="button"
        class="breadcrumbEllipsis"
        icon="ri-more-fill"
        :aria-label="$t('moreActions')"
        rounded
        text
        severity="secondary"
        @click="toggleBreadCrumbMenu($event)"
      />
      <PrimeMenu ref="menuBreadcrumb" :model="menuBreadcrumbItems" popup>
        <template #item="{ item }">
          <div class="p-2">
            <router-link :to="item.route">
              <span class="ml-2 breadcrumbMenuItem" :class="item.icon" />
              <span class="ml-2 breadcrumbMenuItem">{{ item.label }}</span>
            </router-link>
          </div>
        </template>
      </PrimeMenu>

      <i
        v-if="!isVerySmallScreen"
        class="ri-arrow-right-s-line breadcrumbSeparator text-2xl mb-2 mx-1"
      ></i>

      <!-- secondToLastItem (only for large screen) -->
      <div v-if="!isVerySmallScreen">
        <div class="p-menuitem">
          <router-link
            :to="secondToLastItem.to"
            class="flex align-items-center"
          >
            <span
              class="ri-folder-2-line breadcrumbIcon primary-color"
              aria-hidden="true"
            >
            </span>
            <span
              v-tooltip="secondToLastItem.name"
              class="commonElementName breadcrumbElementName"
            >
              {{ secondToLastItem.name }}
            </span>
          </router-link>
        </div>
      </div>

      <i
        class="ri-arrow-right-s-line breadcrumbSeparator text-2xl mb-2 mx-1"
      ></i>

      <!-- Last Item -->
      <div class="p-menuitem">
        <router-link :to="lastItem.to" class="flex align-items-center">
          <span
            class="ri-folder-2-line breadcrumbIcon"
            :class="[
              isInConsultation ? 'primary-color' : 'lastItemElementName',
            ]"
            aria-hidden="true"
          />
          <span
            v-tooltip="lastItem.name"
            class="commonElementName"
            :class="{
              lastItemElementName: !isInConsultation,
              breadcrumbElementName: isInConsultation,
            }"
          >
            {{ lastItem.name }}
          </span>
        </router-link>
      </div>

      <!-- More Actions Button -->
      <PrimeButton
        v-if="!isInSearch && menuActionVisible"
        v-tooltip="$t('moreActions')"
        :aria-label="$t('moreActions')"
        type="button"
        class="breadcrumbDropdown"
        icon="ri-more-2-fill"
        rounded
        text
        severity="secondary"
        @click="toggleMenuActionButton($event, lastItem)"
      />
      <ActionMenu ref="menuAction" :actionMenuItems="menuActionItems" />
    </div>
  </div>
</template>
<script>
import { mapState } from "pinia";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import favoriteService from "@/services/FavoriteService.js";
import menuService from "@/services/MenuService.js";
import workspaceService from "@/services/WorkspaceService.js";

import { useMenuStore } from "@/stores/menu.js";
import { useNavigationStore } from "@/stores/navigation.js";
import { useSelectionStore } from "@/stores/selection.js";
import Button from "primevue/button";
import Menu from "primevue/menu";
import Tooltip from "primevue/tooltip";
import ActionMenu from "../menu/ActionMenu.vue";

export default {
  name: "EllipsisBreadcrumb",
  components: {
    ActionMenu,
    PrimeButton: Button,
    PrimeMenu: Menu,
  },
  directives: {
    tooltip: Tooltip,
  },
  inject: ["getBreadcrumbActions"],
  props: {
    path: {
      type: Array,
      default: () => [],
    },
    currentNode: {
      type: Object,
      default: null,
    },
    isInSearch: {
      type: Boolean,
      default: false,
    },
    isSmallScreen: {
      type: Boolean,
      default: false,
    },
    isVerySmallScreen: {
      type: Boolean,
      default: false,
    },
    menuActionVisible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["reload"],
  data() {
    return {
      popup: null,
      modifyWorkspaceItem: null,
      workspaceName: null,
      menuActionItems: [],
      breadCrumbNode: {},
      modifyPopupTitle: "",
      workspaceNode: null,
      tempAspectsName: null,
      isSite: false,
      menuBreadcrumbItems: [],
    };
  },
  computed: {
    ...mapState(useNavigationStore, ["isInConsultation", "isInUserHome"]),
    lastItem() {
      return this.path[this.path.length - 1];
    },
    secondToLastItem() {
      return this.path[this.path.length - 2];
    },
  },
  methods: {
    toggleMenuActionButton(event, item) {
      const target = event.currentTarget || event.target;
      alfrescoNodeService.getNode(item.id).then((node) => {
        this.menuActionItems = this.getBreadcrumbActions(node);
        this.$refs.menuAction
          .getActionMenuRef()
          .toggle({ currentTarget: target });
      });
    },
    toggleBreadCrumbMenu(event) {
      let pathToDisplay;
      if (!this.isVerySmallScreen && !this.isSmallScreen) {
        pathToDisplay = this.path.slice(2, -2);
      } else if (this.isVerySmallScreen) {
        pathToDisplay = this.path.slice(1, this.path.length - 1);
      } else if (this.isSmallScreen && !this.isVerySmallScreen) {
        pathToDisplay = this.path.slice(2, this.path.length - 2);
      }
      this.menuBreadcrumbItems = pathToDisplay.map((item) => ({
        label: item.name,
        icon:
          item.nodeType === "cm:folder"
            ? "ri-folder-2-line"
            : "ri-building-line",
        route: item.to,
      }));
      this.$refs.menuBreadcrumb.toggle(event);
    },
    isNodeIgnore(item, index) {
      return alfrescoFileService.isPathElementVisible(item, index);
    },
    openModifyWorkspace() {
      workspaceService
        .getWorkspace(this.breadCrumbNode.name)
        .then((workspace) => {
          workspace.name = workspace.id;
          this.modifyWorkspaceItem = workspace;
          this.popup = "MODIFYWORKSPACE";
        });
    },
    openRename() {
      useSelectionStore().selectSingleNode(this.breadCrumbNode);
      this.tempAspectsName = this.breadCrumbNode.aspectNames;
      this.breadCrumbNode.aspectNames = undefined;
      this.modifyPopupTitle = "renameElement";
      this.popup = "MODIFYNODE";
    },
    openModify() {
      useSelectionStore().selectSingleNode(this.breadCrumbNode);
      this.tempAspectsName = this.breadCrumbNode.aspectNames;
      this.modifyPopupTitle = "properties";
      this.popup = "MODIFYNODE";
    },
    openMove() {
      useSelectionStore().selectSingleNode(this.breadCrumbNode);
      this.popup = "MOVENODE";
    },
    openCopy() {
      useSelectionStore().selectSingleNode(this.breadCrumbNode);
      this.popup = "COPYNODE";
    },
    closePopup() {
      if (this.popup === "MODIFYNODE") {
        this.breadCrumbNode.aspectNames = this.tempAspectsName;
      }
      this.popup = null;
      this.$emit("reload");
    },
    openMembers() {
      this.popup = "MEMBERS";
    },
    toggleFavorite() {
      favoriteService
        .changeSingleFavorite(this.breadCrumbNode)
        .then((favorite) => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("menu.favoris"),
            detail: favorite.isFavorite
              ? this.$t("node.addElementFavorite", {
                  name: favorite.name,
                })
              : this.$t("node.removeElementFavorite", {
                  name: favorite.name,
                }),
            life: 3000,
          });
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("node.favoriteError"),
            detail: err.message,
            life: 3000,
          });
        });
    },
    hasRequiredOperationsForOwner() {
      const requiredOperations = ["update", "delete", "updatePermissions"];
      return requiredOperations.every((operation) =>
        this.currentNode.allowableOperations?.includes(operation),
      );
    },
    toggleWorkspaceFavorite() {
      alfrescoNodeService.getNode(this.currentNode.id).then((workspaceNode) => {
        this.breadCrumbNode.isFavorite = workspaceNode.isFavorite;
        workspaceService
          .getWorkspace(this.breadCrumbNode.name)
          .then((workspace) => {
            this.breadCrumbNode.title = workspace.title;
            let menuStore = useMenuStore();
            favoriteService
              .changeSingleFavorite(this.breadCrumbNode)
              .then((favorite) => {
                if (favorite.isFavorite) {
                  menuStore.addFavorite(
                    menuService.siteToMenuItem(favorite),
                    "menuEspaces",
                  );
                  this.breadCrumbNode.isFavorite = favorite.isFavorite;
                  this.$toast.add({
                    severity: "success",
                    summary: this.$t("menu.favoris"),
                    detail: this.$t("node.addSiteFavorite", {
                      name: favorite.title,
                    }),
                    life: 3000,
                  });
                } else {
                  menuStore.removeFavorite(
                    menuService.siteToMenuItem(favorite),
                    "menuEspaces",
                  );
                  this.breadCrumbNode.isFavorite = favorite.isFavorite;
                  this.$toast.add({
                    severity: "success",
                    summary: this.$t("menu.favoris"),
                    detail: this.$t("node.removeSiteFavorite", {
                      name: favorite.title,
                    }),
                    life: 3000,
                  });
                }
              })
              .catch((err) => {
                console.error("erreur de favoris", err);
                this.$toast.add({
                  severity: "error",
                  summary: this.$t("node.favoriteError"),
                  detail: err.message,
                  life: 3000,
                });
              });
          });
      });
    },
  },
};
</script>

<style lang="css" scoped>
.breadcrumbSeparator {
  margin: 0 0.5rem 0 0.5rem;
  height: 1rem;
}
.p-breadcrumb-list {
  flex-wrap: wrap;
}
.breadcrumbDropdown,
.breadcrumbEllipsis {
  vertical-align: unset !important;
  align-items: center !important;
  height: 1.5rem !important;
  width: 1.5rem !important;
  margin-left: -0.2rem !important;
  margin-right: -0.2rem !important;
}
.breadcrumbEllipsis:hover {
  background-color: var(--p-primary-color) !important;
}

.commonElementName {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 11rem;
  align-items: center;
}

.breadcrumbElementName {
  color: var(--p-primary-color);
}

.lastItemElementName {
  color: var(--p-text-color) !important;
  cursor: default;
}

.primary-color {
  color: var(--p-primary-color);
}

.breadcrumbIcon {
  height: auto;
  width: 1.5rem;
  margin: auto;
}
</style>
